import { useCallback } from 'react';
import { capRefusalFor } from '../plans/capRefusal';
import { openTaskManagerBoardTab } from '../store/tabsStore';
import {
  listBoardSummaries,
  readBoard,
  countOwnBoardsAndTasks,
  writeBoard,
  locationForNewBoard,
  LOCATION_DEVICE,
} from '../extensions/bundled/task-manager/boardsStore';
import { announceDeviceItem } from '../records/itemLocation';
import { bumpBoards } from '../extensions/bundled/task-manager/boardsUiStore';
import { aiCreatedMarks, aiEditedMarks } from '../services/ai/aiAuthorship';
import { descriptionPatch } from '../mcp/taskDescription';
import { taskStatusOf, withDoneAt } from '../extensions/bundled/task-manager/taskStatus';
import { taskStatusInput } from '../services/ai/taskStatusInput';
import { subtasksOf, subtasksFromInput } from '../extensions/bundled/task-manager/subtasks';
import { userIdOfScope } from '../extensions/bundled/task-manager/boardCreatedBy';
import { normalizeBoardColor } from '../extensions/bundled/task-manager/boardColorValue';
import { personalScopeId } from '../services/personalScope';
import { joinActiveProjectAfterCreate } from '../services/ai/aiProjectScope';
import { bodyText, genId } from './aiExtensionHelpers';

/** Task Manager tools for the assistant: read boards, create boards / lists / tasks, update tasks. */
export function useAiBoardActions({
  navigateToPanel,
}) {
  // ── Task Manager ──────────────────────────────────────────────────────────

  const findBoardId = useCallback(async ({ board_id, board_name }) => {
    if (board_id) return String(board_id);
    if (board_name) {
      const summaries = await listBoardSummaries();
      const target = String(board_name).trim().toLowerCase();
      const hit = (summaries || []).find((b) => String(b.name || '').trim().toLowerCase() === target);
      return hit ? hit.id : null;
    }
    return null;
  }, []);

  const getBoards = useCallback(async () => {
    const summaries = await listBoardSummaries();
    return {
      boards: (summaries || []).map((b) => ({ id: b.id, name: b.name, task_count: b.taskCount })),
    };
  }, []);

  const getBoard = useCallback(async (input) => {
    const boardId = await findBoardId(input);
    if (!boardId) return { error: 'Board not found' };
    const board = await readBoard(boardId);
    if (!board) return { error: 'Board not found' };
    return {
      board: {
        id: board.id,
        name: board.name,
        description: board.description || '',
        lists: (board.groups || [])
          .slice()
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map((g) => ({ id: g.id, name: g.name, position: g.position ?? 0 })),
        tasks: (board.tasks || []).map((t) => ({
          id: t.id,
          group_id: t.groupId,
          title: t.title || '',
          description: bodyText(t.descriptionHtml).slice(0, 2000),
          priority: t.priority || 'medium',
          // To do / In progress / Done / Dismissed, reconciled with `done`.
          status: taskStatusOf(t),
          due_date: t.dueDate || null,
          done: !!t.done,
          // The checklist, in order; ids are what update_task's list takes back.
          subtasks: subtasksOf(t).map(({ id, title, done }) => ({ id, title, done })),
        })),
      },
    };
  }, [findBoardId]);

  const createBoard = useCallback(async (input) => {
    const capped = await capRefusalFor('boards', async () => (await countOwnBoardsAndTasks()).boards);
    if (capped) return { error: capped };
    const now = new Date().toISOString();
    const boardId = genId();
    const lists = Array.isArray(input.lists) ? input.lists : [];
    const groups = lists
      .filter((name) => typeof name === 'string' && name.trim())
      .map((name, i) => ({ id: genId(), name: name.trim(), position: i }));
    const board = {
      id: boardId,
      name: input.name || 'Untitled Board',
      description: input.description || '',
      color: normalizeBoardColor(input.color),
      showDoneTasks: true,
      // Free plan: to the cloud while there is room, to this device after that
      // (cloud/local items, card C5) — the same rule the New board modal uses.
      location: locationForNewBoard(),
      // The overview tile's owner (extras §2.8): the signed-in user; signed
      // out (`personal:local`) there is nobody to name yet.
      createdBy: userIdOfScope(personalScopeId()),
      createdAt: now,
      updatedAt: now,
      // AI Act Art. 50(2) machine-readable mark plus the turn's provider and
      // model (boardsStore.decomposeBoard carries the keys; aiAuthorship.js).
      ...aiCreatedMarks(),
      groups,
      tasks: [],
    };
    await writeBoard(board);
    if (board.location === LOCATION_DEVICE) announceDeviceItem('boards');
    const project = await joinActiveProjectAfterCreate('board', boardId); // card 6.3
    bumpBoards();
    // Always open the new board — creating one you can't see is pointless
    // (mirrors create_document always opening in Documents). Switches to the
    // Task Manager panel and opens the board tab.
    navigateToPanel('task-manager.panel');
    openTaskManagerBoardTab({ boardId, label: board.name });
    return { board_id: boardId, list_ids: groups.map((g) => g.id), ...(project ? { project } : {}) };
  }, [navigateToPanel]);

  // Read-modify-write a board by id. Returns the mutation's return value.
  const mutateBoard = useCallback(async (boardId, mutator) => {
    const board = await readBoard(boardId);
    if (!board) throw new Error('Board not found');
    const next = { ...board, groups: [...(board.groups || [])], tasks: [...(board.tasks || [])] };
    const ret = mutator(next);
    next.updatedAt = new Date().toISOString();
    await writeBoard(next);
    bumpBoards();
    return ret;
  }, []);

  const createList = useCallback(async (input) => {
    if (!input.board_id) return { error: 'board_id is required' };
    const listId = genId();
    await mutateBoard(String(input.board_id), (board) => {
      board.groups.push({ id: listId, name: input.name || 'New List', position: board.groups.length });
    });
    return { list_id: listId };
  }, [mutateBoard]);

  const createTask = useCallback(async (input) => {
    if (!input.board_id) return { error: 'board_id is required' };
    if (!input.title) return { error: 'title is required' };
    const status = taskStatusInput({ status: input.status });
    if (status.error) return status;
    const checklist = input.subtasks === undefined ? { subtasks: [] } : subtasksFromInput(input.subtasks, []);
    if (checklist.error) return { error: checklist.error.message };
    // Plans v2: a card on the user's OWN (private) board counts against the
    // task cap; a team board's cards belong to the team (as in the UI's + and
    // MCP's board_task_create), the same split countOwnBoardsAndTasks counts.
    const target = await readBoard(String(input.board_id)).catch(() => null);
    if (target?.visibility !== 'team') {
      const capped = await capRefusalFor('tasks', async () => (await countOwnBoardsAndTasks()).tasks);
      if (capped) return { error: capped };
    }
    const taskId = genId();
    try {
      await mutateBoard(String(input.board_id), (board) => {
        let groupId = input.group_id ? String(input.group_id) : null;
        if (!groupId || !board.groups.some((g) => g.id === groupId)) {
          if (board.groups.length === 0) {
            const g = { id: genId(), name: 'To Do', position: 0 };
            board.groups.push(g);
            groupId = g.id;
          } else {
            groupId = board.groups[0].id;
          }
        }
        const now = new Date().toISOString();
        const position = board.tasks.filter((t) => t.groupId === groupId).length;
        // Keys the model sends are stripped; a new task has no images to keep.
        const descriptionHtml = input.description ? descriptionPatch(input.description, input.format).descriptionHtml : '';
        board.tasks.push({
          id: taskId,
          groupId,
          position,
          title: input.title,
          descriptionHtml,
          priority: input.priority || 'medium',
          dueDate: input.due_date || null,
          done: false,
          ...status.patch,
          ...(status.patch.done === true ? { doneAt: now } : {}),
          createdAt: now,
          updatedAt: now,
          comments: [],
          subtasks: checklist.subtasks,
          // AI Act Art. 50(2) machine-readable mark with the turn's provider,
          // model and a signature of the description; a task's data is spread
          // on save (decomposeBoard), so it survives later edits.
          ...aiCreatedMarks(descriptionHtml),
        });
      });
    } catch (err) {
      return { error: err.message };
    }
    return { task_id: taskId };
  }, [mutateBoard]);

  // Locate a task across all boards (task_id alone), or within board_id when given.
  const locateTaskBoard = useCallback(async (taskId, boardHint) => {
    if (boardHint) {
      const board = await readBoard(String(boardHint));
      if (board && (board.tasks || []).some((t) => t.id === taskId)) return board.id;
    }
    const summaries = await listBoardSummaries();
    for (const s of summaries || []) {
      const board = await readBoard(s.id);
      if (board && (board.tasks || []).some((t) => t.id === taskId)) return board.id;
    }
    return null;
  }, []);

  // `patch` is an object, or a function of the stored task (read inside the
  // board's read-modify-write) returning one.
  const applyTaskPatch = useCallback(async (input, patch) => {
    const taskId = String(input.task_id);
    const boardId = await locateTaskBoard(taskId, input.board_id);
    if (!boardId) return { error: 'Task not found' };
    await mutateBoard(boardId, (board) => {
      board.tasks = board.tasks.map((t) =>
        t.id === taskId
          ? { ...t, ...withDoneAt(t, typeof patch === 'function' ? patch(t) : patch), updatedAt: new Date().toISOString() }
          : t
      );
    });
    return { success: true, task_id: taskId };
  }, [locateTaskBoard, mutateBoard]);

  const updateTask = useCallback(async (input) => {
    const patch = {};
    if (input.title != null) patch.title = input.title;
    if (input.priority != null) patch.priority = input.priority;
    if (input.due_date !== undefined) patch.dueDate = input.due_date;
    if (input.group_id != null) patch.groupId = String(input.group_id);
    const status = taskStatusInput(input);
    if (status.error) return status;
    Object.assign(patch, status.patch);
    const description = input.description != null;
    const checklist = input.subtasks !== undefined;
    if (checklist && !Array.isArray(input.subtasks)) return { error: 'subtasks must be an array' };
    if (!description && !checklist && Object.keys(patch).length === 0) return { error: 'No fields to update' };
    // A description write keeps the task's images and clears its stale block
    // snapshot (src/mcp/taskDescription.js, shared with MCP board_task_update).
    // A subtasks list REPLACES the checklist; it is checked against the task's
    // own list (ids must exist), and a refusal is thrown so nothing is written.
    const refusal = new Error('subtasks refused');
    try {
      return await applyTaskPatch(input, (task) => {
        const next = { ...patch };
        if (description) {
          Object.assign(next, descriptionPatch(input.description, input.format, task));
          // AI Act Art. 50(2): the AI rewrote this task's content.
          Object.assign(next, aiEditedMarks(next.descriptionHtml));
        }
        if (checklist) {
          const r = subtasksFromInput(input.subtasks, subtasksOf(task));
          if (r.error) {
            refusal.message = r.error.message;
            throw refusal; // before mutateBoard's write
          }
          next.subtasks = r.subtasks;
        }
        return next;
      });
    } catch (err) {
      if (err === refusal) return { error: err.message };
      throw err;
    }
  }, [applyTaskPatch]);

  const writeTaskDescription = useCallback(async (input) => {
    if (input.content == null) return { error: 'content is required' };
    return applyTaskPatch(input, (task) => {
      const next = descriptionPatch(input.content, input.format, task);
      // AI Act Art. 50(2): machine-readable mark of the AI edit, beside (never
      // over) the task's own createdAt / human fields.
      return { ...next, ...aiEditedMarks(next.descriptionHtml) };
    });
  }, [applyTaskPatch]);

  return {
    findBoardId,
    getBoards,
    getBoard,
    createBoard,
    mutateBoard,
    createList,
    createTask,
    updateTask,
    writeTaskDescription,
  };
}
